import { useAuth } from "@/core/auth/hooks";
import { fieldErrorsFromZod } from "@/core/auth/utils";
import { Badge } from "@/modules/shadcn/ui/badge";
import { Button } from "@/modules/shadcn/ui/button";
import { Input } from "@/modules/shadcn/ui/input";
import { Label } from "@/modules/shadcn/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/modules/shadcn/ui/select";
import { Spinner } from "@/modules/shadcn/ui/spinner";
import {
  inviteUserSchema,
  OrgMemberRoleEnum,
  UpdateMemberRole,
  type AssignableRole,
} from "@repo/schema";
import { X } from "lucide-react";
import { FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { orgRoleBadge, orgRoleLabel } from "../../constants/roleLabels";
import {
  useGetInvitationsQuery,
  useGetMembersQuery,
  useInviteUserMutation,
  useRemoveMemberMutation,
  useRevokeInvitationMutation,
  useUpdateMemberRoleMutation,
} from "../../services";
import { apiErrorMessage } from "@/core/api";
import { formatDate } from "@/core/i18n";

export const MemberList = () => {
  const { t } = useTranslation(["account", "common"]);
  const { user, role: currentUserRole } = useAuth();
  const { data: members, isLoading } = useGetMembersQuery();
  const [inviteUser, { isLoading: isInviting }] = useInviteUserMutation();
  const [removeMember] = useRemoveMemberMutation();
  const [updateMemberRole] = useUpdateMemberRoleMutation();
  const canManage =
    currentUserRole === OrgMemberRoleEnum.ADMIN ||
    currentUserRole === OrgMemberRoleEnum.OWNER;
  const { data: invitations } = useGetInvitationsQuery(undefined, {
    skip: !canManage,
  });
  const [revokeInvitation] = useRevokeInvitationMutation();
  const [inviteRole, setInviteRole] = useState<AssignableRole>(OrgMemberRoleEnum.MEMBER);
  const [inviteEmailError, setInviteEmailError] = useState<string | undefined>();

  const handleInvite = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const email = formData.get("email") as string;
    const parsed = inviteUserSchema.safeParse({ email, role: inviteRole });
    if (!parsed.success) {
      setInviteEmailError(fieldErrorsFromZod(parsed.error).email);
      return;
    }
    setInviteEmailError(undefined);

    try {
      await inviteUser(parsed.data).unwrap();
      form.reset();
      setInviteRole(OrgMemberRoleEnum.MEMBER);
      toast.success(t("members.invitationSent", { email }));
    } catch (err: unknown) {
      toast.error(apiErrorMessage(err, t("members.invitationFailed")));
    }
  };

  const handleRoleChange = async (userId: number, role: string) => {
    try {
      await updateMemberRole({
        userId,
        role: role as UpdateMemberRole["role"],
      }).unwrap();
      toast.success(t("members.roleUpdated"));
    } catch {
      toast.error(t("members.roleUpdateFailed"));
    }
  };

  const handleRemove = async (userId: number) => {
    try {
      await removeMember(userId).unwrap();
      toast.success(t("members.memberRemoved"));
    } catch {
      toast.error(t("members.memberRemoveFailed"));
    }
  };

  const handleRevoke = async (invitationId: number) => {
    try {
      await revokeInvitation(invitationId).unwrap();
      toast.success(t("members.invitationRevoked"));
    } catch {
      toast.error(t("members.invitationRevokeFailed"));
    }
  };

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <div className="flex flex-col gap-6">
      <h5 className="text-xl font-semibold">{t("members.title")}</h5>

      <div className="flex flex-col gap-3">
        {members?.map((member) => (
          <div
            key={member.user.id}
            className="flex items-center justify-between border-b border-black/10 py-3 last:border-b-0"
          >
            <div className="flex flex-col gap-0.5">
              <span>{member.user.fullName}</span>
              <span className="text-sm text-muted-foreground">
                {member.user.email}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {canManage && member.role !== OrgMemberRoleEnum.OWNER ? (
                <>
                  <Select
                    value={member.role}
                    onValueChange={(val) =>
                      val && handleRoleChange(member.user.id, val)
                    }
                  >
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder={t("members.rolePlaceholder")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={OrgMemberRoleEnum.ADMIN}>
                        {t(orgRoleLabel[OrgMemberRoleEnum.ADMIN])}
                      </SelectItem>
                      <SelectItem value={OrgMemberRoleEnum.MEMBER}>
                        {t(orgRoleLabel[OrgMemberRoleEnum.MEMBER])}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  {member.user.id !== user?.id && (
                    <button
                      type="button"
                      className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-gray-100 hover:text-destructive"
                      onClick={() => handleRemove(member.user.id)}
                      aria-label={t("members.removeMember")}
                    >
                      <X className="size-4" />
                    </button>
                  )}
                </>
              ) : (
                <Badge
                  variant={
                    member.role === OrgMemberRoleEnum.MEMBER
                      ? "secondary"
                      : "default"
                  }
                >
                  {t(orgRoleBadge[member.role])}
                </Badge>
              )}
            </div>
          </div>
        ))}
      </div>

      {canManage && invitations && invitations.length > 0 && (
        <>
          <div className="my-2 h-px bg-black/10" />
          <h5 className="text-xl font-semibold">{t("members.pendingInvitations")}</h5>

          <div className="flex flex-col gap-3">
            {invitations.map((invitation) => (
              <div
                key={invitation.id}
                className="flex items-center justify-between border-b border-black/10 py-3 last:border-b-0"
              >
                <div className="flex flex-col gap-0.5">
                  <span>{invitation.email}</span>
                  <span className="text-sm text-muted-foreground">
                    {t("members.invitedOn", {
                      date: formatDate(invitation.createdAt, "date"),
                    })}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">
                    {t(
                      orgRoleLabel[
                        invitation.role === OrgMemberRoleEnum.ADMIN
                          ? OrgMemberRoleEnum.ADMIN
                          : OrgMemberRoleEnum.MEMBER
                      ],
                    )}
                  </Badge>
                  <Badge variant="outline">{t("members.pending")}</Badge>
                  <button
                    type="button"
                    className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-gray-100 hover:text-destructive"
                    onClick={() => handleRevoke(invitation.id)}
                    aria-label={t("members.revokeInvitation")}
                  >
                    <X className="size-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {canManage && (
        <>
          <div className="my-2 h-px bg-black/10" />
          <h5 className="text-xl font-semibold">{t("members.inviteTitle")}</h5>

          <form onSubmit={handleInvite} noValidate>
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="invite-email">{t("members.email")}</Label>
                <div className="flex flex-wrap items-center gap-2">
                  <Input
                    id="invite-email"
                    name="email"
                    type="email"
                    placeholder={t("members.emailPlaceholder")}
                    aria-invalid={!!inviteEmailError || undefined}
                    onChange={() => setInviteEmailError(undefined)}
                    className="min-w-0 flex-1"
                  />
                  <Select
                    value={inviteRole}
                    onValueChange={(val) => setInviteRole(val as AssignableRole)}
                  >
                    <SelectTrigger className="w-36">
                      <SelectValue placeholder={t("members.rolePlaceholder")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={OrgMemberRoleEnum.MEMBER}>
                        {t(orgRoleLabel[OrgMemberRoleEnum.MEMBER])}
                      </SelectItem>
                      <SelectItem value={OrgMemberRoleEnum.ADMIN}>
                        {t(orgRoleLabel[OrgMemberRoleEnum.ADMIN])}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {inviteEmailError && (
                  <p className="text-xs text-destructive">{inviteEmailError}</p>
                )}
              </div>
              <Button
                type="submit"
                disabled={isInviting}
                className="w-fit"
              >
                {isInviting ? t("members.sending") : t("members.sendInvitation")}
              </Button>
            </div>
          </form>
        </>
      )}
    </div>
  );
};
